import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { normalizeLang } from "./languages";

// Bazowe zasoby synchronizowane w pakiecie – reszta ładowana leniwie per język
const resources = {
  pl: {
    common: {
      loading: "Ładowanie…",
      logout: "Wyloguj",
      theme_light: "Przełącz na jasny motyw",
      theme_dark: "Przełącz na ciemny motyw",
      filters: "Filtry",
      clearFilters: "Wyczyść filtry",
    },
  },
  en: {
    common: {
      loading: "Loading…",
      logout: "Logout",
      theme_light: "Switch to light theme",
      theme_dark: "Switch to dark theme",
      filters: "Filters",
      clearFilters: "Clear filters",
    },
  },
  fr: {
    common: {
      loading: "Chargement…",
      logout: "Déconnexion",
      theme_light: "Basculer en thème clair",
      theme_dark: "Basculer en thème sombre",
      filters: "Filtres",
      clearFilters: "Effacer les filtres",
    },
  },
  de: {
    common: {
      loading: "Laden…",
      logout: "Abmelden",
      theme_light: "Zum hellen Theme wechseln",
      theme_dark: "Zum dunklen Theme wechseln",
      filters: "Filter",
      clearFilters: "Filter löschen",
    },
  },
  it: {
    common: {
      loading: "Caricamento…",
      logout: "Disconnetti",
      theme_light: "Passa al tema chiaro",
      theme_dark: "Passa al tema scuro",
      filters: "Filtri",
      clearFilters: "Cancella filtri",
    },
  },
  es: {
    common: {
      loading: "Cargando…",
      logout: "Cerrar sesión",
      theme_light: "Cambiar a tema claro",
      theme_dark: "Cambiar a tema oscuro",
      filters: "Filtros",
      clearFilters: "Borrar filtros",
    },
  },
  sk: {
    common: {
      loading: "Načítavam…",
      logout: "Odhlásiť",
      theme_light: "Prepnúť na svetlú tému",
      theme_dark: "Prepnúť na tmavú tému",
      filters: "Filtre",
      clearFilters: "Vymazať filtre",
    },
  },
  uk: {
    common: {
      loading: "Завантаження…",
      logout: "Вийти",
      theme_light: "Перемкнути на світлу тему",
      theme_dark: "Перемкнути на темну тему",
      filters: "Фільтри",
      clearFilters: "Очистити фільтри",
    },
  },
};

// Przygotowanie mapy plików językowych -> leniwe loadery
const moduleLoaders = import.meta.glob("./resources/*/*.json");
const namespaceLoaders = {};

for (const path in moduleLoaders) {
  const match = path.match(/\.\/resources\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const lang = match[1];
  const ns = match[2];
  namespaceLoaders[lang] = namespaceLoaders[lang] || {};
  namespaceLoaders[lang][ns] = moduleLoaders[path];
}

const loadedNamespaces = new Map();

function markNamespaceLoaded(lang, ns) {
  if (!loadedNamespaces.has(lang)) {
    loadedNamespaces.set(lang, new Set());
  }
  loadedNamespaces.get(lang).add(ns);
}

function namespaceAlreadyLoaded(lang, ns) {
  return loadedNamespaces.has(lang) && loadedNamespaces.get(lang).has(ns);
}

async function loadNamespacesForLanguage(lang, namespaces) {
  const loadersForLang = namespaceLoaders[lang];
  if (!loadersForLang) return null;

  const targets =
    namespaces && namespaces.length ? namespaces : Object.keys(loadersForLang);
  const toLoad = targets.filter(
    (ns) => loadersForLang[ns] && !namespaceAlreadyLoaded(lang, ns),
  );
  if (!toLoad.length) return null;

  const entries = await Promise.all(
    toLoad.map(async (ns) => {
      const mod = await loadersForLang[ns]();
      const data = mod.default || mod;
      markNamespaceLoaded(lang, ns);
      return [ns, data];
    }),
  );

  return Object.fromEntries(entries);
}

const stored =
  (typeof localStorage !== "undefined" && localStorage.getItem("app.lang")) ||
  null;
const queryLang = (() => {
  try {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search || "");
    return params.get("lang");
  } catch (e) {
    return null;
  }
})();
const initial = normalizeLang(
  queryLang ||
    stored ||
    (typeof navigator !== "undefined" ? navigator.language : "pl"),
);

// Załaduj eagerly tylko pakiet językowy dla języka startowego
const initialBundles = await loadNamespacesForLanguage(initial);
if (initialBundles) {
  resources[initial] = { ...(resources[initial] || {}), ...initialBundles };
}

i18n.use(initReactI18next).init({
  lng: initial,
  fallbackLng: "pl",
  resources,
  defaultNS: "common",
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

async function ensureLanguageLoaded(lang, namespaces) {
  const data = await loadNamespacesForLanguage(lang, namespaces);
  if (!data) return;
  Object.entries(data).forEach(([ns, bundle]) => {
    i18n.addResourceBundle(lang, ns, bundle, true, true);
  });
}

const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async function (lang, ...rest) {
  await ensureLanguageLoaded(lang);
  return originalChangeLanguage(lang, ...rest);
};

// Zachowujemy loadNamespace, ale teraz faktycznie ładuje brakujące bundle
export async function loadNamespace(ns, lang = i18n.language) {
  if (!ns) return;
  if (i18n.hasResourceBundle(lang, ns)) return;
  await ensureLanguageLoaded(lang, [ns]);
}

export default i18n;
